import { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { ConfigContext } from '../../App';
import { TEAMS_CONFIG, URLS, IMS_EVENT } from '../../constants';
import { SubscriptionService } from '../../models/subscription-service';
import Spinner from '../spinner/spinner';
import OMEGA from '../../omega';
import './teamssetup.scss';

declare global {
    interface Window {
        adobeIMS: any;
        msal: any; // Change this to match the existing declaration
    }
}

// MSAL configuration
const msalConfig = {
    auth: {
        clientId: TEAMS_CONFIG.CLIENT_ID,
        authority: TEAMS_CONFIG.MICROSOFT_LOGIN_URL,
        redirectUri: window.location.origin + '/teams-setup',
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri: window.location.origin + '/teams/landing'
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
    },
    system: {
        allowNativeBroker: false, // Disable native broker
        allowRedirectInIframe: true
    }
};

const msalInstance = new PublicClientApplication(msalConfig);

// Attach MSAL instance to window for global access
window.msal = msalInstance;

export default function TeamsSetup() {
    const locales = useContext(ConfigContext).appConfig.languageData;
    const history = useHistory();
    
    const [hasStarted, setHasStarted] = useState(false); // Simple flag to prevent multiple starts
    const [subscriptionComplete, setSubscriptionComplete] = useState(false); // Track subscription completion
    
    // Get Microsoft ID from sessionStorage (set by previous page)
    const microsoftId = sessionStorage.getItem('teams_setup_microsoft_id');

    const subscriptionService: SubscriptionService = SubscriptionService.init();
    
    const eventDefaultValue = {
        element: "Teams Setup",
        action: "error",
        type: "Component",
        widget: {
            name: "teamssetup",
            type: "component"
        }
    }

    function omega_capture(element: any, widget_name: any, widget_type: any) {
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: element,
            widget: {
                name: widget_name,
                type: widget_type
            },
        }]);
    }

    // Set up message listener immediately when component mounts
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'SUBSCRIPTION_COMPLETE' && event.source === window.opener) {
                console.log('Received subscription complete message from parent window');
                setSubscriptionComplete(true);
            }
        };
        
        window.addEventListener('message', handleMessage);
        
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    // Simple function to register Teams user
    const registerTeamsUser = async (userDetails: any) => {
        try {
            
            const payload = {
                teamsId: userDetails.aadObjectId,
                userName: userDetails.name,
                tenantId: userDetails.tenantId,
                installationStatus: userDetails.installationStatus
            };
            
            // Call the API to register the Teams user
            const response = await subscriptionService.addTeamsProfile(payload);
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error('Failed to register Teams user:', error);
            omega_capture(
                `Error registering Teams user: ${error.message || error.toString()}`,
                "teamssetup",
                "error"
            );
            throw error;
        }
    };

    // Function to wait for subscription completion from parent window
    const waitForSubscriptionComplete = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const maxWaitTime = 10000; // 10 seconds max wait
            const checkInterval = 100; // Check every 100ms
            let elapsedTime = 0;
            
            // If subscription is already complete, resolve immediately
            if (subscriptionComplete) {
                resolve();
                return;
            }
            
            // Otherwise, wait for the state to change
            const checkSubscription = () => {
                if (subscriptionComplete) {
                    resolve();
                } else {
                    elapsedTime += checkInterval;
                    if (elapsedTime >= maxWaitTime) {
                        console.warn('Timeout waiting for subscription completion, proceeding anyway');
                        resolve(); // Proceed anyway rather than failing
                    } else {
                        // Check again in 100ms
                        setTimeout(checkSubscription, checkInterval);
                    }
                }
            };
            
            checkSubscription();
        });
    };

    // Simple function to handle auth error
    const handleAuthError = async () => {
        const userDetails = {
            aadObjectId: microsoftId,
            installationStatus: 5
        };
        // Register user and redirect to Teams
        await registerTeamsUser(userDetails);
        history.push('/teams/error');
    };

    // Main authentication function
    const doAuthentication = async () => {
        try {

            //console.log('Starting authentication with Microsoft ID:', microsoftId);
            // Initialize MSAL
            await msalInstance.initialize();
            
            // Check if we have auth response (redirect back from Microsoft)
            const response = await msalInstance.handleRedirectPromise();
            
            if (response && response.account) {
                // We got auth response - user is authenticated
                const userDetails = {
                    name: response.account.name || response.account.username || 'Unknown User',
                    email: response.account.username,
                    tenantId: response.account.tenantId,
                    userId: response.account.localAccountId,
                    homeAccountId: response.account.homeAccountId,
                    aadObjectId: response.account.localAccountId
                };
                
                // Wait for subscription completion from parent window
                await waitForSubscriptionComplete();
                
                // Register user and redirect to Teams
                await registerTeamsUser(userDetails);
                window.location.href = URLS.ADD_TO_TEAMS_URL;
                return;
            }
            
            // First, try to find existing account that matches the loginHint
            const accounts = msalInstance.getAllAccounts();
            //console.log('Microsoft ID from session:', microsoftId);
            //console.log('All accounts found:', accounts.map(acc => acc.username));
            const targetAccount = accounts.find(account => 
                account.username?.toLowerCase() === microsoftId?.toLowerCase()
            );
            
            if (targetAccount) {
                //console.log('Target account found in MSAL cache:', targetAccount.username);
                //console.log('Using existing account data directly (no token acquisition needed)');
                
                // Directly use the existing account data - no need for token acquisition
                const userDetails = {
                    name: targetAccount.name || targetAccount.username || 'Unknown User',
                    email: targetAccount.username,
                    tenantId: targetAccount.tenantId,
                    userId: targetAccount.localAccountId,
                    homeAccountId: targetAccount.homeAccountId,
                    aadObjectId: targetAccount.localAccountId
                };
                
                //console.log('Existing authentication found - User details:', userDetails);
                
                // Wait for subscription completion from parent window
                await waitForSubscriptionComplete();
                
                // Register user and redirect to Teams
                await registerTeamsUser(userDetails);
                window.location.href = URLS.ADD_TO_TEAMS_URL;
                return;
            } else {
                //console.log('No matching account found in MSAL cache for:', microsoftId);
                //console.log('Trying silent SSO with browser session...');
                
                // Try silent SSO first - this can use browser's existing Microsoft session
                try {
                    //console.log('Attempting ssoSilent with loginHint:', microsoftId);
                    const ssoRequest = {
                        scopes: ["openid", "profile", "email", "User.Read"],
                        loginHint: microsoftId || ''
                    };
                    
                    const ssoResult = await msalInstance.ssoSilent(ssoRequest);
                    
                    // Process the SSO result
                    const userDetails = {
                        name: ssoResult.account.name || ssoResult.account.username || 'Unknown User',
                        email: ssoResult.account.username,
                        tenantId: ssoResult.account.tenantId,
                        userId: ssoResult.account.localAccountId,
                        homeAccountId: ssoResult.account.homeAccountId,
                        aadObjectId: ssoResult.account.localAccountId
                    };
                    
                    // Wait for subscription completion from parent window
                    await waitForSubscriptionComplete();
                    
                    // Register user and redirect to Teams
                    await registerTeamsUser(userDetails);
                    window.location.href = URLS.ADD_TO_TEAMS_URL;
                    return;
                    
                } catch (ssoError: any) {
                    //console.log('Silent SSO failed:', ssoError.errorCode || ssoError.message);
                    console.log('Falling back to interactive login');
                    omega_capture(
                        `Teams SSO silent auth failed: ${ssoError.errorCode || ssoError.message || ssoError.toString()}`,
                        "teamssetup",
                        "error"
                    );
                    // Fall through to interactive login below
                }
            }
            
            // No existing account or silent auth failed, use interactive login
            const loginRequest = {
                scopes: ["openid", "profile", "email", "User.Read"],
                loginHint: microsoftId || undefined,
                prompt: microsoftId ? 'login' : 'consent' // Use consent if no Microsoft ID, select_account to choose account
            };
            
            await msalInstance.loginRedirect(loginRequest);
            
        } catch (error: any) {
            console.error('Authentication error:', error);
            omega_capture(
                `Teams authentication error: ${error.message || error.toString()}`,
                "teamssetup",
                "error"
            );
            handleAuthError();
        }
    };

    useEffect(() => {
        // Only run once
        if (hasStarted) {
            return;
        }
        
        const startAuthentication = async () => {
            // Clear any existing Microsoft authentication first
            //await clearMicrosoftAuth();
            
            // Mark as started and begin authentication
            setHasStarted(true);
            doAuthentication();
        };
        
        // Wait for Adobe IMS to be ready before starting authentication
        if (window.adobeIMS) {
            // Adobe IMS is ready, start authentication
            startAuthentication();
        } else {
            // Wait for Adobe IMS to load
            const handleIMSReady = () => {
                startAuthentication();
            };
            
            document.addEventListener(IMS_EVENT, handleIMSReady);
            
            // Cleanup event listener
            return () => {
                document.removeEventListener(IMS_EVENT, handleIMSReady);
            };
        }
        
    }, []); // Empty dependency array - only run once

    // Simple loading UI
    return (
        <div className="teams-setup-container">
            <div className="status-stripe teams-stripe">
                <div className="container">
                    {locales.teams_setup_title || 'Microsoft Teams Setup'}
                </div>
            </div>
            <div className="teams-setup-wizard">
                <div className="container">
                    <div className="teams-setup-content">
                        <h2>{locales.teams_authenticating || 'Authenticating with Microsoft'}</h2>
                        <p>{locales.teams_auth_message || `Signing you in with ${microsoftId}...`}</p>
                        <Spinner />
                    </div>
                </div>
            </div>
        </div>
    );
} 